import { PaymentContainer } from 'src/components/PaymentContainer/PaymentContainer';
import { PaymentGenericError } from 'src/components/PaymentGenericError/PaymentGenericError';
import { ListingProductCode, type ListingProduct } from 'src/generated/graphql';
import {
  isEmailBoostAdPack,
  isEmailBoostStandard,
  isSearchBoost,
} from 'src/utils/listingProducts';

import { AcceleratePaymentScreen } from './components/PaymentScreens/AcceleratePaymentScreen';
import { EmailBoostPaymentScreen } from './components/PaymentScreens/EmailBoostPaymentScreen';
import { SearchBoostPaymentScreen } from './components/PaymentScreens/SearchBoostPaymentScreen';
import { StandardPaymentScreen } from './components/PaymentScreens/StandardPaymentScreen';
import { StripeProider } from './components/StripeProvider/StripeProvider';

interface PaymentSingleProductProps {
  clientId: string;
  firstUrlProduct: ListingProduct;
  singleProducts: {
    freemium: ListingProduct;
    standard: ListingProduct;
    accelerate: ListingProduct;
    searchBoostAdPack: ListingProduct;
    searchBoostStandard: ListingProduct;
    emailBoostAdPack: ListingProduct;
    emailBoostStandard: ListingProduct;
  };
  combinedProducts: {
    standardAndSearchBoost: ListingProduct;
    emailBoostAndSearchBoostAdPack: ListingProduct;
    emailBoostAndSearchBoostStandard: ListingProduct;
  };
  isListingSearchBoosted: boolean;
  listingId: string | null;
}

export const PaymentSingleProduct = (props: PaymentSingleProductProps) => {
  if (props.firstUrlProduct.productCode === ListingProductCode.ListUntilSold) {
    return (
      <PaymentContainer width="medium">
        <StripeProider>
          <StandardPaymentScreen
            standardProduct={props.firstUrlProduct}
            searchBoostProduct={props.singleProducts.searchBoostStandard}
            standardAndSearchBoost={
              props.combinedProducts.standardAndSearchBoost
            }
            clientId={props.clientId}
            listingId={props.listingId}
          />
        </StripeProider>
      </PaymentContainer>
    );
  }

  if (props.firstUrlProduct.productCode === ListingProductCode.Accelerate) {
    return (
      <PaymentContainer width="medium">
        <StripeProider>
          <AcceleratePaymentScreen
            accelerateProduct={props.firstUrlProduct}
            clientId={props.clientId}
            listingId={props.listingId}
          />
        </StripeProider>
      </PaymentContainer>
    );
  }

  if (isEmailBoostAdPack(props.firstUrlProduct)) {
    return (
      <PaymentContainer width="medium">
        <StripeProider>
          <EmailBoostPaymentScreen
            emailBoostProduct={props.firstUrlProduct}
            searchBoostProduct={props.singleProducts.searchBoostAdPack}
            emailBoostAndSearchBoostProduct={
              props.combinedProducts.emailBoostAndSearchBoostAdPack
            }
            isListingSearchBoosted={props.isListingSearchBoosted}
            clientId={props.clientId}
            listingId={props.listingId}
          />
        </StripeProider>
      </PaymentContainer>
    );
  }

  if (isEmailBoostStandard(props.firstUrlProduct)) {
    return (
      <PaymentContainer width="medium">
        <StripeProider>
          <EmailBoostPaymentScreen
            emailBoostProduct={props.firstUrlProduct}
            searchBoostProduct={props.singleProducts.searchBoostStandard}
            emailBoostAndSearchBoostProduct={
              props.combinedProducts.emailBoostAndSearchBoostStandard
            }
            isListingSearchBoosted={props.isListingSearchBoosted}
            clientId={props.clientId}
            listingId={props.listingId}
          />
        </StripeProider>
      </PaymentContainer>
    );
  }

  // We don't need to bundle this with any other products so we don't need to
  // distinguish between the underlying listing type: ad pack or standard
  if (isSearchBoost(props.firstUrlProduct)) {
    return (
      <PaymentContainer width="medium">
        <StripeProider>
          <SearchBoostPaymentScreen
            searchBoostProduct={props.firstUrlProduct}
            clientId={props.clientId}
            listingId={props.listingId}
          />
        </StripeProider>
      </PaymentContainer>
    );
  }

  // TODO unit test
  return <PaymentGenericError testId="single-product-no-match-error" />;
};
